import React, { type ReactElement, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { type MapDispatchToProps, type MapStateToProps, connect } from 'react-redux';

import { emptyMap, messages } from '../constants';
import * as D from '../deps';

import AlbumGridItemThirdLine from './common/AlbumGridItemThirdLine';

const {
  GridItem,
  getBackstagePathForToken,
  getArt,
  PandoraTypes,
  getInDomain,
  getSourceCardMenuItems,
  isRadioOnly,
  isUnavailable,
  getBackstageLocation,
} = D;

export interface IBackstageAlbumGridItemStateProps {
  collaborationName: string;
  collaborationUrl: string;
  onDemandIsEnabled: ReturnType<typeof D.Utils.Listener.hasPremiumFeatures>;
  albumBackstagePath: string;
  artUrl: string;
  albumTitle: string;
  trackCount: number;
  isAlbumPlaying: boolean;
  isAlbumPaused: boolean;
  isRadioOnly: boolean;
  isUnavailable: ReturnType<typeof isUnavailable>;
  isClean: boolean;
  isExplicit: boolean;
  year: string;
  isCollected: boolean;
  sourceCardMenuItems: $TSFixMe[];
}

export interface IBackstageAlbumGridItemDispatchProps {
  pauseTrack: typeof D.Actions.UseCases.pauseTrack;
  playResumeSource: typeof D.playResumeSource;
}

export interface IBackstageAlbumGridItemOwnProps {
  pandoraId: string;
  albumSeoToken?: string;
  dominantColor?: string;
  isDisabled?: boolean;
  isFluid?: boolean;
  isLarge?: boolean;
  isMobileWeb?: boolean;
  isPlaceholder?: boolean;
  musicId?: string;
  showPremiumAccess?: boolean;
  twoColumns?: boolean;
}

export type IBackstageAlbumGridItemProps = IBackstageAlbumGridItemStateProps &
  IBackstageAlbumGridItemDispatchProps &
  IBackstageAlbumGridItemOwnProps;

export class BackstageAlbumGridItem extends React.Component<IBackstageAlbumGridItemProps> {
  static contextType = D.AppContext;

  onPlayButtonClick = (): void => {
    // @ts-expect-error TS(2339) FIXME: Property 'playResumeSource' does not exist on type... Remove this comment to see the full error message
    this.props.playResumeSource({
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      forceRestartSourceIfPlaying: this.props.showPremiumAccess,
      id: this.props.pandoraId,
      pageUrl: this.props.albumBackstagePath,
      pandoraType: null,
      shouldShuffle: false,
    });
  };

  onPauseButtonClick = (): void => {
    if (this.props.onDemandIsEnabled) {
      this.props.pauseTrack();
    }
  };

  additionalIntegrationContext = (): { pandoraId: IBackstageAlbumGridItemProps['pandoraId'] } => {
    return { pandoraId: this.props.pandoraId };
  };

  getBadge = (): ReactElement | null => {
    const { isClean, isExplicit } = this.props;

    if (isClean) {
      return <D.Badge badgeType={D.BadgeTypes.CLEAN} />;
    } else if (isExplicit) {
      return <D.Badge badgeType={D.BadgeTypes.EXPLICIT_SHORT} />;
    }

    return null;
  };

  render(): ReactNode {
    const albumBackstagePath = this.props.albumSeoToken
      ? getBackstagePathForToken(this.props.albumSeoToken)
      : getBackstageLocation({
          path: this.props.albumBackstagePath,
          pandoraId: this.props.pandoraId,
        });

    return (
      <D.AppContextIntegrationData data={this.additionalIntegrationContext}>
        <GridItem
          imgUrl={this.props.artUrl}
          itemOnclickUrl={albumBackstagePath}
          mainText={this.props.albumTitle}
          mainTextUrl={albumBackstagePath}
          secondText={
            <FormattedMessage
              {...messages.totalSongs}
              values={{ trackCount: this.props.trackCount }}
            />
          }
          thirdText={
            <AlbumGridItemThirdLine
              preText={this.props.year}
              mainText={this.props.collaborationName}
              mainTextUrl={this.props.collaborationUrl}
              Badge={this.getBadge()}
            />
          }
          isPlaceholder={this.props.isPlaceholder}
          isPlaying={this.props.isAlbumPlaying}
          isPaused={this.props.isAlbumPaused}
          sourceCardMenuItems={this.props.sourceCardMenuItems}
          dominantColor={this.props.dominantColor}
          playButtonOnclick={this.onPlayButtonClick}
          pauseButtonOnclick={this.onPauseButtonClick}
          isDisabled={
            (!this.props.onDemandIsEnabled && !this.props.showPremiumAccess) ||
            this.props.isRadioOnly
          }
          isCollected={this.props.isCollected}
          isRadioOnly={this.props.isRadioOnly}
          isUnavailable={this.props.isUnavailable}
          isLarge={this.props.isLarge}
          isOnlyArt={this.props.isMobileWeb}
          twoColumns={this.props.twoColumns}
          isFluid={this.props.isFluid}
        />
      </D.AppContextIntegrationData>
    );
  }
}

function getArtistCollaborationProps(_state: $TSFixMe, annotation = emptyMap): $TSFixMe {
  const isCollaboration = annotation.get('collaboration');

  return {
    collaborationName: isCollaboration ? annotation.get('name') : null,
    collaborationUrl: isCollaboration ? annotation.get('shareableUrlPath') : null,
  };
}

// @ts-expect-error TS(7006) FIXME: Parameter 'state' implicitly has an 'any' type.
export const mapStateToProps: MapStateToProps<
  IBackstageAlbumGridItemStateProps,
  IBackstageAlbumGridItemOwnProps,
  $TSFixMe
> = (state, ownProps) => {
  const getAnnotations = getInDomain('catalog.utils.getAnnotations');
  const isAlbumPlayingCurrently = getInDomain('playback.utils.isSourcePlayingCurrently', false);
  const isPandoraIdInCollection = getInDomain('collection.utils.isPandoraIdInCollection', false);
  const isClean = getInDomain('catalog.utils.isClean');
  const isExplicit = getInDomain('catalog.utils.isExplicit');
  const isAudioPlaying = getInDomain('playback.utils.isAudioPlaying', false);

  const pandoraId = ownProps.pandoraId;
  const props = {};

  const albumAnnotations = getAnnotations(state, pandoraId);
  if (albumAnnotations && !albumAnnotations.isEmpty()) {
    const audioIsPlaying = isAudioPlaying(state);
    const isAlbumActive = isAlbumPlayingCurrently(state, pandoraId);
    const artistId = albumAnnotations.get('artistId');
    const artistAnnotations = getAnnotations(state, artistId);
    const { collaborationName, collaborationUrl } = getArtistCollaborationProps(
      state,
      artistAnnotations,
    );

    // @ts-expect-error TS(2339) FIXME: Property 'collaborationName' does not exist on typ... Remove this comment to see the full error message
    props.collaborationName = collaborationName;
    // @ts-expect-error TS(2339) FIXME: Property 'collaborationUrl' does not exist on type... Remove this comment to see the full error message
    props.collaborationUrl = collaborationUrl;
    // @ts-expect-error TS(2339) FIXME: Property 'onDemandIsEnabled' does not exist on typ... Remove this comment to see the full error message
    props.onDemandIsEnabled = D.Utils.Listener.hasPremiumFeatures(state);
    // @ts-expect-error TS(2339) FIXME: Property 'albumBackstagePath' does not exist on ty... Remove this comment to see the full error message
    props.albumBackstagePath = albumAnnotations.get('shareableUrlPath');
    // @ts-expect-error TS(2339) FIXME: Property 'artUrl' does not exist on type '{}'.
    props.artUrl = getArt(albumAnnotations, 500, state);
    // @ts-expect-error TS(2339) FIXME: Property 'albumTitle' does not exist on type '{}'.
    props.albumTitle = albumAnnotations.get('name');
    // @ts-expect-error TS(2339) FIXME: Property 'trackCount' does not exist on type '{}'.
    props.trackCount = albumAnnotations.get('trackCount');
    // @ts-expect-error TS(2339) FIXME: Property 'isAlbumPlaying' does not exist on type '... Remove this comment to see the full error message
    props.isAlbumPlaying = isAlbumActive && audioIsPlaying;
    // @ts-expect-error TS(2339) FIXME: Property 'isAlbumPaused' does not exist on type '{... Remove this comment to see the full error message
    props.isAlbumPaused = isAlbumActive && !audioIsPlaying;
    // @ts-expect-error TS(2339) FIXME: Property 'isRadioOnly' does not exist on type '{}'... Remove this comment to see the full error message
    props.isRadioOnly = isRadioOnly(albumAnnotations);
    // @ts-expect-error TS(2339) FIXME: Property 'isUnavailable' does not exist on type '{... Remove this comment to see the full error message
    props.isUnavailable = isUnavailable(albumAnnotations);
    // @ts-expect-error TS(2339) FIXME: Property 'isClean' does not exist on type '{}'.
    props.isClean = isClean(albumAnnotations);
    // @ts-expect-error TS(2339) FIXME: Property 'isExplicit' does not exist on type '{}'.
    props.isExplicit = isExplicit(albumAnnotations);

    if (albumAnnotations.get('releaseDate')) {
      // @ts-expect-error TS(2339) FIXME: Property 'year' does not exist on type '{}'.
      props.year = new Date(albumAnnotations.get('releaseDate')).getUTCFullYear().toString();
    }

    // @ts-expect-error TS(2339) FIXME: Property 'isCollected' does not exist on type '{}'... Remove this comment to see the full error message
    props.isCollected = isPandoraIdInCollection(state, pandoraId);
    // @ts-expect-error TS(2339) FIXME: Property 'sourceCardMenuItems' does not exist on t... Remove this comment to see the full error message
    props.sourceCardMenuItems = getSourceCardMenuItems({
      pandoraId,
      pandoraType: PandoraTypes.ALBUM,
      shareParentSource: {
        shareParentSourceId: pandoraId,
        shareParentSourceType: PandoraTypes.ALBUM,
      },
      state,
    });

    // @ts-expect-error TS(2339) FIXME: Property 'dominantColor' does not exist on type '{... Remove this comment to see the full error message
    props.dominantColor = D.Utils.Art.getDominantColor(albumAnnotations);
  } else {
    // @ts-expect-error TS(2339) FIXME: Property 'isPlaceholder' does not exist on type '{... Remove this comment to see the full error message
    props.isPlaceholder = true;
  }

  // @ts-expect-error TS(2339) FIXME: Property 'showPremiumAccess' does not exist on typ... Remove this comment to see the full error message
  props.showPremiumAccess = D.Utils.Listener.showPremiumAccess(state);
  // @ts-expect-error TS(2339) FIXME: Property 'isMobileWeb' does not exist on type '{}'... Remove this comment to see the full error message
  props.isMobileWeb = D.isMobileWeb(state);

  return props;
};

const mapDispatchToProps: MapDispatchToProps<
  IBackstageAlbumGridItemDispatchProps,
  IBackstageAlbumGridItemOwnProps
> = {
  pauseTrack: D.Actions.UseCases.pauseTrack,
  playResumeSource: D.playResumeSource,
};

export default connect(mapStateToProps, mapDispatchToProps)(BackstageAlbumGridItem);
